import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
  Switch,
  FormGroup,
  FormControlLabel,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const benefitsData = [
  {
    feature: "Career Analysis Report",
    description: "Detailed analysis and insights",
    free: true,
    limited: true,
    standard: true,
    platinum: true,
  },
  {
    feature: "Career Summary",
    description: "One-page summary of your career report",
    free: true,
    limited: true,
    standard: true,
    platinum: true,
  },
  {
    feature: "Job Hunting Toolkit",
    description: "Useful tools and shortcuts for job hunting",
    free: false,
    limited: true,
    standard: true,
    platinum: true,
  },
  {
    feature: "Mid-Career Switch Course",
    description: "Online course with 5-step framework",
    free: false,
    limited: true,
    standard: true,
    platinum: true,
  },
  {
    feature: "Dream Job Course",
    description: "Comprehensive guide to landing your ideal role",
    free: false,
    limited: true,
    standard: true,
    platinum: true,
  },
  {
    feature: "Resume Templates",
    description: "Download industry-specific resume samples",
    free: false,
    limited: true,
    standard: true,
    platinum: true,
  },

  {
    feature: "Coach Carol AI",
    description: "Access to AI-powered career coach",
    free: false,
    limited: false,
    standard: false,
    platinum: true,
  },
  {
    feature: "Networking Events",
    description: "Access to in-person networking events",
    free: false,
    limited: false,
    standard: false,
    platinum: true,
  },
];

const plans = [
  {
    id: "free",
    name: "Free",
    priceMonthly: "$0",
    priceYearly: "$0",
    description: "Basic career tools",
    buttonText: "Your Current Plan",
    isDisabled: true,
    highlight: false,
    tierMonthly: null,
    tierYearly: null,
  },
  {
    id: "limited",
    name: "90-Day Access",
    priceMonthly: "$9.90",
    priceYearly: "$9.90",
    description:
      "One-time payment, valid for 90 days. Includes essential tools.",
    buttonText: "90-Day Access (One-Time Payment)",
    isDisabled: false,
    highlight: false,
    badge: "New",
    tierMonthly: "6",
    tierYearly: "5",
  },
  {
    id: "standard",
    name: "Standard",
    priceMonthly: "$7.90",
    priceYearly: "$39.90",
    description: "Complete toolkit for job seekers",
    buttonText: "Subscribe Now",
    tierMonthly: "3",
    tierYearly: "1",
    highlight: true,
    popular: true,
  },
  {
    id: "platinum",
    name: "Platinum",
    priceMonthly: "$9.90",
    priceYearly: "$49.90",
    description: "Premium career acceleration package",
    buttonText: "Subscribe Now",
    tierMonthly: "4",
    tierYearly: "2",
    highlight: false,
  },
];

const BenefitsComparison = ({ variant = "dashboard", userId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedPlan, setSelectedPlan] = useState(1); // Default to Standard plan
  const [isAnnual, setIsAnnual] = useState(true); // Default to annual billing
  const [userTier, setUserTier] = useState(0); // Default to Free tier
  const [loading, setLoading] = useState(true);
  const [currentPlans, setCurrentPlans] = useState([...plans]);
  const navigate = useNavigate();

  // Fetch user's current tier
  useEffect(() => {
    const fetchUserTier = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/${userId}/upgrade-status`
        );

        // Set the user's current tier
        const currentTier = response.data.tier; // 0, 1, 2, 3, or 4
        setUserTier(currentTier);

        // Update plans to mark the current plan
        updateCurrentPlan(currentTier);
      } catch (error) {
        console.error("Error fetching user tier:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTier();
  }, [userId]);

  // Update plans to show the current plan indicator
  const updateCurrentPlan = (tier) => {
    const updatedPlans = plans.map((plan) => {
      // Reset all plans
      const updatedPlan = {
        ...plan,
        buttonText:
          plan.id === "free"
            ? "Your Current Plan"
            : plan.id === "limited"
            ? plan.buttonText
            : "Subscribe Now",
        isDisabled: plan.id === "free",
        highlight: plan.id === "standard", // Standard is highlighted by default
      };

      // Update based on current tier
      if (tier === 0 && plan.id === "free") {
        // Free tier - already set correctly
        return updatedPlan;
      } else if ((tier === 1 || tier === 3) && plan.id === "standard") {
        // Standard tier (annual or monthly)
        return {
          ...updatedPlan,
          buttonText: "Your Current Plan",
          isDisabled: true,
        };
      } else if ((tier === 2 || tier === 4) && plan.id === "platinum") {
        // Platinum tier (annual or monthly)
        return {
          ...updatedPlan,
          buttonText: "Your Current Plan",
          isDisabled: true,
          highlight: true, // Highlight current plan
        };
      }

      // For non-current plans that are above the user's tier, keep Subscribe Now
      // For non-current plans that are below the user's tier (e.g., Free when on Standard),
      // hide button if it's the Free tier, otherwise show "Downgrade"
      if (
        (tier === 1 || tier === 3 || tier === 2 || tier === 4) &&
        plan.id === "free"
      ) {
        return {
          ...updatedPlan,
          buttonText: "", // Empty string to hide the button
          isDisabled: true,
          showButton: false, // Custom flag to hide button completely
        };
      } else if ((tier === 2 || tier === 4) && plan.id === "standard") {
        return {
          ...updatedPlan,
          buttonText: "Downgrade",
          isDisabled: true,
        };
      }

      return updatedPlan;
    });

    setCurrentPlans(updatedPlans);
  };

  const handlePlanChange = (event, newValue) => {
    setSelectedPlan(newValue);
  };

  const handleBillingToggle = () => {
    setIsAnnual(!isAnnual);
  };

  const handleUpgrade = async (tier) => {
    if (!userId) {
      console.error("User ID is missing");
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            userId: userId,
            tier: tier,
          }),
        }
      );
      const { id: sessionId, message } = await response.json();

      if (!response.ok) {
        console.error(
          "Error creating checkout session:",
          message || `HTTP error! status: ${response.status}`
        );
        return;
      }

      if (!sessionId) {
        console.error("Checkout session ID not received from backend.");
        return;
      }

      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      if (!stripe) {
        console.error("Stripe.js failed to load.");
        return;
      }

      await stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      console.error("Error during upgrade process:", error);
    }
  };

  // Filter out the Free tier for rendering
  const visiblePlans = currentPlans.filter((plan) => plan.id !== "free");

  // Billing toggle component
  const renderBillingToggle = () => {
    // Calculate savings percentage based on monthly vs annual pricing
    const calculateSavings = () => {
      const standardMonthly =
        parseFloat(plans[1].priceMonthly.replace("$", "")) * 12;
      const standardYearly = parseFloat(plans[1].priceYearly.replace("$", ""));
      const standardSavings = Math.round(
        ((standardMonthly - standardYearly) / standardMonthly) * 100
      );

      const platinumMonthly =
        parseFloat(plans[2].priceMonthly.replace("$", "")) * 12;
      const platinumYearly = parseFloat(plans[2].priceYearly.replace("$", ""));
      const platinumSavings = Math.round(
        ((platinumMonthly - platinumYearly) / platinumMonthly) * 100
      );

      return Math.max(standardSavings, platinumSavings);
    };

    const savingsPercent = calculateSavings();

    return (
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <FormGroup sx={{ justifyContent: "center", alignItems: "center" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Monthly</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isAnnual}
                  onChange={handleBillingToggle}
                  color="primary"
                />
              }
              label=""
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>Annual</Typography>
              <Chip
                label={`Save up to ${savingsPercent}%`}
                size="small"
                color="success"
                sx={{
                  fontWeight: 600,
                  display: isAnnual ? "flex" : "none",
                }}
              />
            </Stack>
          </Stack>
        </FormGroup>
      </Box>
    );
  };

  // Mobile view - plan cards with tabs
  const renderMobileView = () => {
    // Calculate savings percentage for the selected plan
    const calculateSavings = (plan) => {
      if (!plan.priceMonthly || !plan.priceYearly) return 0;
      const monthlyPrice = parseFloat(plan.priceMonthly.replace("$", "")) * 12;
      const yearlyPrice = parseFloat(plan.priceYearly.replace("$", ""));
      return Math.round(((monthlyPrice - yearlyPrice) / monthlyPrice) * 100);
    };

    const savingsPercent = calculateSavings(visiblePlans[selectedPlan]);

    return (
      <Box sx={{ width: "100%" }}>
        <Tabs
          value={selectedPlan}
          onChange={handlePlanChange}
          variant="fullWidth"
          sx={{ mb: 3 }}
          TabIndicatorProps={{
            style: { backgroundColor: theme.palette.primary.main },
          }}
        >
          {visiblePlans.map((plan, index) => (
            <Tab
              key={plan.id}
              label={plan.name}
              sx={{
                fontWeight: plan.highlight ? 700 : 400,
                color: plan.highlight ? "primary.main" : "inherit",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
                transition: "color 0.3s, background-color 0.3s",
              }}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Box sx={{ position: "relative" }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {visiblePlans[selectedPlan].name}
              {visiblePlans[selectedPlan].popular && (
                <Chip
                  label="MOST POPULAR"
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
              {visiblePlans[selectedPlan].badge && (
                <Chip
                  label={visiblePlans[selectedPlan].badge}
                  size="small"
                  color="secondary"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {/* Add Current Plan indicator */}
            {visiblePlans[selectedPlan].buttonText === "Current Plan" && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bgcolor: "success.main",
                  color: "white",
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                }}
              ></Box>
            )}
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h4"
              fontWeight={700}
              color="primary.main"
              gutterBottom
            >
              {isAnnual
                ? visiblePlans[selectedPlan].priceYearly
                : visiblePlans[selectedPlan].priceMonthly}
            </Typography>
            {isAnnual &&
              savingsPercent > 0 &&
              visiblePlans[selectedPlan].id !== "limited" && (
                <Chip
                  label={`Save ${savingsPercent}%`}
                  size="small"
                  color="success"
                  sx={{
                    fontWeight: 600,
                    height: "auto",
                    py: 0.5,
                  }}
                />
              )}
          </Stack>
          <Typography variant="body2" color="text.secondary" paragraph>
            {visiblePlans[selectedPlan].id === "limited"
              ? null
              : isAnnual
              ? `Billed annually`
              : `Billed monthly`}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {visiblePlans[selectedPlan].description}
          </Typography>

          {visiblePlans[selectedPlan][
            isAnnual ? "tierYearly" : "tierMonthly"
          ] &&
            visiblePlans[selectedPlan].showButton !== false && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={visiblePlans[selectedPlan].isDisabled}
                sx={{
                  bgcolor: visiblePlans[selectedPlan].highlight
                    ? "primary.main"
                    : "#000",
                  py: 1.5,
                  mt: 2,
                  mb: 3,
                  "&:hover": {
                    bgcolor: visiblePlans[selectedPlan].highlight
                      ? "primary.dark"
                      : "#333",
                  },
                }}
                onClick={() =>
                  handleUpgrade(
                    visiblePlans[selectedPlan][
                      isAnnual ? "tierYearly" : "tierMonthly"
                    ]
                  )
                }
              >
                {visiblePlans[selectedPlan].buttonText}
              </Button>
            )}

          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            sx={{ mt: 2 }}
          >
            Features:
          </Typography>
          <List disablePadding>
            {benefitsData
              .filter((benefit) => {
                // For Platinum, only show features exclusive to Platinum
                if (selectedPlan === 2)
                  return benefit.platinum && !benefit.standard;
                // For Standard, only show features available in Standard but not in Free
                if (selectedPlan === 1)
                  return benefit.standard && !benefit.free;
                // For Limited, only show features available in Limited but not in Free
                if (
                  selectedPlan === 0 &&
                  visiblePlans[selectedPlan].id === "limited"
                )
                  return benefit.limited && !benefit.free;
                // For Free, only show features available in Free
                return benefit.free;
              })
              .map((benefit) => (
                <ListItem key={benefit.feature} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit.feature}
                    secondary={variant === "page" ? benefit.description : null}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                    }}
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </Box>
    );
  };

  // Desktop view - comparison cards
  const renderDesktopView = () => {
    // Calculate savings percentage for each plan
    const calculateSavings = (plan) => {
      if (!plan.priceMonthly || !plan.priceYearly) return 0;
      const monthlyPrice = parseFloat(plan.priceMonthly.replace("$", "")) * 12;
      const yearlyPrice = parseFloat(plan.priceYearly.replace("$", ""));
      return Math.round(((monthlyPrice - yearlyPrice) / monthlyPrice) * 100);
    };

    return (
      <Grid container spacing={2}>
        {visiblePlans.map((plan, index) => {
          // Filter benefits for each plan
          const planBenefits = benefitsData.filter((benefit) => {
            if (plan.id === "platinum")
              return benefit.platinum && !benefit.standard;
            if (plan.id === "standard")
              return benefit.standard && !benefit.free;
            if (plan.id === "limited") return benefit.limited && !benefit.free;
            return benefit[plan.id];
          });

          const savingsPercent = calculateSavings(plan);

          return (
            <Grid item xs={12} md={4} key={plan.id}>
              <Paper
                elevation={plan.highlight ? 6 : 1}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: plan.highlight
                    ? `2px solid ${theme.palette.primary.main}`
                    : null,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 25,
                      right: -30,
                      transform: "rotate(45deg)",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 0.5,
                      width: 150,
                      textAlign: "center",
                      boxShadow: 1,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600}>
                      MOST POPULAR
                    </Typography>
                  </Box>
                )}

                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {plan.name}
                  {plan.badge && (
                    <Chip
                      label={plan.badge}
                      size="small"
                      color="secondary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="primary.main"
                    gutterBottom
                  >
                    {isAnnual ? plan.priceYearly : plan.priceMonthly}
                  </Typography>
                  {isAnnual && savingsPercent > 0 && plan.id !== "limited" && (
                    <Chip
                      label={`Save ${savingsPercent}%`}
                      size="small"
                      color="success"
                      sx={{
                        fontWeight: 600,
                        height: "auto",
                        py: 0.5,
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {plan.id === "limited"
                    ? null
                    : isAnnual
                    ? `Billed annually`
                    : `Billed monthly`}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {plan.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <List disablePadding sx={{ flexGrow: 1 }}>
                  {planBenefits.map((benefit) => (
                    <ListItem
                      key={benefit.feature}
                      disablePadding
                      sx={{ py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit.feature}
                        secondary={
                          variant === "page" ? benefit.description : null
                        }
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          variant: "caption",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 1 }}>
                  {plan.showButton !== false && (
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={plan.isDisabled}
                      sx={{
                        bgcolor: plan.highlight ? "primary.main" : "#000",
                        py: 1.5,
                        "&:hover": {
                          bgcolor: plan.highlight ? "primary.dark" : "#333",
                        },
                      }}
                      onClick={() =>
                        plan[isAnnual ? "tierYearly" : "tierMonthly"] &&
                        handleUpgrade(
                          plan[isAnnual ? "tierYearly" : "tierMonthly"]
                        )
                      }
                    >
                      {plan.buttonText}
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: variant === "dashboard" ? "#F2F6FF" : "white",
        borderRadius: 3,
        overflow: "visible",
        position: "relative",
      }}
    >
      {variant === "page" && (
        <>
          <IconButton
            onClick={() => navigate(`/dashboard/${userId}`)}
            sx={{
              position: "absolute",
              left: 16,
              top: 16,
              color: "text.secondary",
              bgcolor: "rgba(0,0,0,0.03)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.08)",
              },
            }}
            aria-label="Back to dashboard"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ mb: 4, mt: 2, textAlign: "center" }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Choose Your Career Plan
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select the plan that best helps your career advancement
            </Typography>
          </Box>
        </>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {renderBillingToggle()}
          {isMobile ? renderMobileView() : renderDesktopView()}
        </>
      )}
    </Box>
  );
};

export default BenefitsComparison;
